"use client";
import * as React from "react";
import { twMerge } from "tailwind-merge";
import { useElementSize } from "@/hooks/useElementSize";
import { DetailView } from "./components/DetailView";
import { NodeList } from "./components/NodeList";
import { HierarchyContextInstance } from "./context";
import { SingleLayout } from "./layouts/SingleLayout";
import { SplitLayout } from "./layouts/SplitLayout";
import type { HierarchyContext, HierarchyExplorerProps, HierarchyNode, StickyNodeState } from "./types";
import { formatHeight, validateHierarchyData } from "./utils";

/**
 * HierarchyExplorer - Main component for drill-down navigation through hierarchical data
 */
export function HierarchyExplorer<T extends HierarchyNode = HierarchyNode>({
  data,
  renderNode,
  onNodeSelect,
  onNavigate,
  selectedId: controlledSelectedId,
  rootNodeId,
  stickyRootNode = true,
  height,
  minHeight,
  maxHeight,
  variant = "subtle",
  size = "md",
  showBullets = true,
  layout = "auto",
  splitBreakpoint = 768,
  renderEmptyState,
  renderDetailView,
  className,
  "aria-label": ariaLabel = "Hierarchy explorer",
}: HierarchyExplorerProps<T>) {
  // Internal state for uncontrolled mode
  const [internalSelectedId, setInternalSelectedId] = React.useState<string | null>(null);
  const [navigationStack, setNavigationStack] = React.useState<T[]>([]);
  const [slideDirection, setSlideDirection] = React.useState<"left" | "right">("left");
  const [rootNodeStickyState, setRootNodeStickyState] = React.useState<StickyNodeState<T>>({
    position: null,
    node: null,
  });

  // Container size detection for responsive layout
  const { ref: containerRef, width: containerWidth } = useElementSize<HTMLDivElement>();

  // Determine if we should use split layout based on container width and layout prop
  const useSplitLayout = React.useMemo(() => {
    if (layout === "split") return true;
    if (layout === "single") return false;
    // Auto mode: use split layout if container is wide enough
    return containerWidth >= splitBreakpoint;
  }, [layout, containerWidth, splitBreakpoint]);

  // Use controlled state if provided, otherwise use internal state
  const selectedId = controlledSelectedId !== undefined ? controlledSelectedId : internalSelectedId;

  // Validate unique IDs and root node existence (dev only)
  React.useEffect(() => {
    validateHierarchyData(data, rootNodeId);
  }, [data, rootNodeId]);

  // Get current view based on navigation stack
  const currentParent = navigationStack.length > 0 ? navigationStack[navigationStack.length - 1] : null;
  const currentItems = currentParent ? (currentParent.children as T[]) || [] : data;

  const navigateToNode = React.useCallback(
    (node: T) => {
      setSlideDirection("left");
      setNavigationStack((prev) => [...prev, node]);
      onNavigate?.(node, [...navigationStack, node]);
    },
    [navigationStack, onNavigate]
  );

  const navigateBack = React.useCallback(() => {
    setSlideDirection("right");
    setNavigationStack((prev) => {
      const newStack = prev.slice(0, -1);
      onNavigate?.(newStack.length > 0 ? newStack[newStack.length - 1] : null, newStack);
      return newStack;
    });
  }, [onNavigate]);

  const selectNode = React.useCallback(
    (node: T) => {
      // Update internal state if uncontrolled
      if (controlledSelectedId === undefined) {
        setInternalSelectedId(node.id);
      }

      // Call callback with current path
      onNodeSelect?.(node, [...navigationStack, node]);
    },
    [controlledSelectedId, navigationStack, onNodeSelect]
  );

  const setRootNodeSticky = React.useCallback((position: "top" | "bottom" | null, node: T | null) => {
    setRootNodeStickyState({ position, node });
  }, []);

  const contextValue = React.useMemo<HierarchyContext<T>>(
    () => ({
      selectedId,
      selectNode,
      navigateToNode,
      navigateBack,
      rootNodeId,
      stickyRootNode,
      variant,
      size,
      showBullets,
      onNodeSelect,
      onNavigate,
      navigationStack,
      setRootNodeSticky,
      useSplitLayout,
    }),
    [
      selectedId,
      selectNode,
      navigateToNode,
      navigateBack,
      rootNodeId,
      stickyRootNode,
      variant,
      size,
      showBullets,
      onNodeSelect,
      onNavigate,
      navigationStack,
      setRootNodeSticky,
      useSplitLayout,
    ]
  );

  // Scroll root node to center after initial render (only in single-pane mode)
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!rootNodeId || !scrollContainerRef.current || navigationStack.length > 0 || useSplitLayout) return;

    const container = scrollContainerRef.current;

    const timeoutId = setTimeout(() => {
      const rootElement = container.querySelector(`[data-node-id="${rootNodeId}"]`) as HTMLElement;

      if (rootElement) {
        rootElement.scrollIntoView({
          behavior: "auto",
          block: "center",
          inline: "nearest",
        });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [rootNodeId, navigationStack.length, useSplitLayout]);

  const containerStyle: React.CSSProperties = {
    height: formatHeight(height),
    minHeight: formatHeight(minHeight),
    maxHeight: formatHeight(maxHeight),
  };

  // Determine if we need overflow for scrolling
  const hasHeight = height !== undefined || maxHeight !== undefined;

  // Generate a unique key for the current view to trigger animations
  const viewKey = currentParent ? currentParent.id : "root";

  // Render a list of nodes with vertical connector line
  const renderNodeList = React.useCallback(
    (items: T[], showConnector = false) => (
      <NodeList
        items={items}
        showConnector={showConnector}
        scrollContainerRef={showConnector ? scrollContainerRef : null}
        showBullets={showBullets}
        size={size}
        renderNode={renderNode}
      />
    ),
    [showBullets, size, renderNode]
  );

  // Render detail pane content (metadata + children)
  const renderDetailContent = React.useCallback(
    (parent: T | null) => {
      if (!parent) return null;

      // Use custom renderer if provided, otherwise use default
      if (renderDetailView) {
        return renderDetailView(parent, renderNodeList);
      }

      return <DetailView node={parent} renderNodeList={renderNodeList} />;
    },
    [renderDetailView, renderNodeList]
  );

  return (
    <HierarchyContextInstance.Provider value={contextValue}>
      <div
        ref={containerRef}
        className={twMerge(
          "select-none bg-background-surface rounded-lg border border-border-default relative",
          hasHeight && "overflow-hidden",
          !hasHeight && "h-full flex flex-col", // Fill parent and use flex when no explicit height is set
          useSplitLayout && !hasHeight && "flex-row", // Override to flex-row for split layout
          useSplitLayout && hasHeight && "flex",
          className
        )}
        style={containerStyle}
        role="tree"
        aria-label={ariaLabel}
      >
        {useSplitLayout ? (
          <SplitLayout
            data={data}
            currentItems={currentItems}
            navigationStack={navigationStack}
            selectedId={selectedId}
            variant={variant}
            size={size}
            showBullets={showBullets}
            rootNodeStickyState={rootNodeStickyState}
            renderNodeList={renderNodeList}
            renderDetailContent={renderDetailContent}
            renderEmptyState={renderEmptyState}
            onNavigateBack={navigateBack}
          />
        ) : (
          <SingleLayout
            currentItems={currentItems}
            currentParent={currentParent}
            navigationStack={navigationStack}
            selectedId={selectedId}
            variant={variant}
            size={size}
            showBullets={showBullets}
            slideDirection={slideDirection}
            viewKey={viewKey}
            hasHeight={hasHeight}
            rootNodeStickyState={rootNodeStickyState}
            renderNodeList={renderNodeList}
            renderDetailContent={renderDetailContent}
            onNavigateBack={navigateBack}
          />
        )}
      </div>
    </HierarchyContextInstance.Provider>
  );
}

// Re-export types for convenience
export type {
  HierarchyExplorerProps,
  HierarchyNode,
  NodeState,
} from "./types";
